import classNames from 'classnames';
import { Icon, IconName } from '@components/icon';
import { Body } from '@components/text';

import './TabItem.scss';

export interface TabItemProps extends Pick<React.HTMLAttributes<HTMLDivElement>, 'role'> {
  /** id to reference the element */
  id: string;
  /** Key name to reference the tabItem */
  tabKey: string;
  /** String for text type */
  label?: string;
  /** Icon name for icon type */
  icon?: IconName;
  /** Optional aria label for accessibility */
  ariaLabel?: string;
  /** Show the proper style based on the state, default state as default */
  state?: 'disabled' | 'active' | 'default';
  /** Optional classname for the component */
  className?: string;
  /** ReactNode to be used as tabItem content */
  children?: React.ReactNode;
  /** A click event callback */
  onClick?: (key: string) => void;
}

/**
 * TabItem Component, used for TabPane component.
 * @param id - Id to reference the element.
 * @param tabKey - Key name to reference the tabItem.
 * @param label - String for text type.
 * @param icon - Icon name for icon type.
 * @param ariaLabel - optional aria label for accessibility.
 * @param state - Show the proper style based on the state, default state as default.
 * @param className - Optional classname for the component.
 * @param children - ReactNode to be used as tabItem content.
 * @param onClick - A click event callback.
 */

export const TabItem: React.FC<TabItemProps> = ({
  id,
  tabKey,
  label,
  className,
  state = 'default',
  icon,
  ariaLabel,
  children,
  onClick,
  ...rest
}) => {
  const handleTabItemClick = (tabKey: string) => {
    if (state === 'disabled') return;
    onClick?.(tabKey);
  };

  return (
    <div
      id={id}
      key={tabKey}
      className={classNames('vds-tab-item', {
        'vds-tab-item--active': state === 'active',
        'vds-tab-item--disabled': state === 'disabled',
        className,
      })}
      onClick={() => handleTabItemClick(tabKey)}
      {...rest}>
      <div
        className="vds-tab-item__content"
        tabIndex={state === 'disabled' ? -1 : 0}
        aria-label={ariaLabel}>
        {children}
        {icon && (
          <Body
            variant="caption"
            size="medium"
            color={state === 'disabled' ? 'disabled' : 'primary'}>
            <Icon name={icon} size="small" />
          </Body>
        )}
        {label && (
          <Body
            variant="caption"
            size="medium"
            color={state === 'disabled' ? 'disabled' : 'primary'}>
            {label}
          </Body>
        )}
      </div>
    </div>
  );
};

export default TabItem;
